
import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { foundersData } from "@/data/founders";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Instagram, Linkedin, Twitter } from "lucide-react";

const JourneyPage: React.FC = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Header */}
      <header className="pt-32 pb-12 bg-palsustain-beige/30">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-palsustain-green mb-4">
            {t("journey.title")}
          </h1>
          <p className="text-lg md:text-xl text-palsustain-charcoal/80 max-w-3xl mx-auto">
            {t("journey.subtitle")}
          </p>
        </div>
      </header>
      
      {/* Timeline */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="relative mx-auto max-w-3xl">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-palsustain-beige"></div>
            
            {/* Timeline items */}
            <div className="mb-20 relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 -top-2 w-6 h-6 rounded-full bg-palsustain-green border-4 border-white z-10"></div>
              
              <div className="relative z-0 pl-0 md:pl-0 pb-6">
                <div className="bg-white rounded-xl shadow-md p-6 md:mx-12 mb-10 transition-all duration-300 hover:shadow-lg">
                  <span className="text-sm font-medium text-palsustain-coral mb-2 block">2022</span>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">
                    {language === "en" ? "The Idea Takes Root" : "تتأصل الفكرة"}
                  </h3>
                  <p className="text-palsustain-charcoal/80">
                    {language === "en" 
                      ? "Shahd and Aseel meet during a sustainable development workshop at Birzeit University. They discover a shared passion for storytelling and documentation of Palestinian sustainability efforts." 
                      : "تلتقي شهد وأسيل خلال ورشة عمل للتنمية المستدامة في جامعة بيرزيت. يكتشفون شغفًا مشتركًا برواية القصص وتوثيق جهود الاستدامة الفلسطينية."
                    }
                  </p>
                </div>
              </div>
              
              <div className="relative z-0 pl-0 md:pl-0 pb-6">
                <div className="bg-white rounded-xl shadow-md p-6 md:mx-12 mb-10 transition-all duration-300 hover:shadow-lg md:ml-32">
                  <span className="text-sm font-medium text-palsustain-coral mb-2 block">2023</span>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">
                    {language === "en" ? "Research & Development" : "البحث والتطوير"}
                  </h3>
                  <p className="text-palsustain-charcoal/80">
                    {language === "en" 
                      ? "The founders conduct extensive research on SDGs in the Palestinian context, collecting stories and mapping sustainability initiatives across the West Bank and Gaza." 
                      : "يجري المؤسسون بحثًا مكثفًا حول أهداف التنمية المستدامة في السياق الفلسطيني، ويجمعون القصص ويرسمون خرائط لمبادرات الاستدامة في جميع أنحاء الضفة الغربية وغزة."
                    }
                  </p>
                </div>
              </div>
              
              <div className="relative z-0 pl-0 md:pl-0 pb-6">
                <div className="bg-white rounded-xl shadow-md p-6 md:mx-12 mb-10 transition-all duration-300 hover:shadow-lg">
                  <span className="text-sm font-medium text-palsustain-coral mb-2 block">2024</span>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">
                    {language === "en" ? "Palsustain Launch" : "إطلاق بالسستين"}
                  </h3>
                  <p className="text-palsustain-charcoal/80">
                    {language === "en" 
                      ? "After securing initial funding through a youth innovation grant, Palsustain is officially launched with its Instagram page and digital platform. The initiative quickly gains attention for its unique approach to storytelling through the SDG framework." 
                      : "بعد تأمين التمويل الأولي من خلال منحة ابتكار الشباب، تم إطلاق بالسستين رسميًا مع صفحتها على Instagram ومنصتها الرقمية. تكتسب المبادرة بسرعة الاهتمام لنهجها الفريد في رواية القصص من خلال إطار أهداف التنمية المستدامة."
                    }
                  </p>
                </div>
              </div>
              
              <div className="relative z-0 pl-0 md:pl-0 pb-6">
                <div className="bg-palsustain-green/10 border border-palsustain-green/20 rounded-xl p-6 md:mx-12 mb-10 transition-all duration-300 hover:shadow-lg md:ml-32">
                  <span className="text-sm font-medium text-palsustain-coral mb-2 block">{language === "en" ? "Present & Future" : "الحاضر والمستقبل"}</span>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">
                    {language === "en" ? "Growing Impact" : "تنمية التأثير"}
                  </h3>
                  <p className="text-palsustain-charcoal/80">
                    {language === "en" 
                      ? "Today, Palsustain continues to expand its network of contributors and storytellers, with an ambitious goal to document sustainability stories from every region of Palestine. Future plans include interactive workshops, educational resources, and collaborative projects with international partners." 
                      : "اليوم، تواصل بالسستين توسيع شبكتها من المساهمين ورواة القصص، بهدف طموح لتوثيق قصص الاستدامة من كل منطقة في فلسطين. تشمل الخطط المستقبلية ورش عمل تفاعلية، وموارد تعليمية، ومشاريع تعاونية مع شركاء دوليين."
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Founders */}
      <section className="py-12 bg-palsustain-cream/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-palsustain-green mb-12 text-center">
            {language === "en" ? "Meet Our Founders" : "تعرف على مؤسسينا"}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {foundersData.map((founder) => (
              <div key={founder.id} className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col md:flex-row">
                <div className="md:w-2/5">
                  <img 
                    src={founder.image} 
                    alt={language === "en" ? founder.name.en : founder.name.ar}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="p-6 md:w-3/5">
                  <h3 className="text-2xl font-bold mb-1">
                    {language === "en" ? founder.name.en : founder.name.ar}
                  </h3>
                  <p className="text-palsustain-charcoal/70 mb-4">
                    {language === "en" ? founder.role.en : founder.role.ar}
                  </p>
                  
                  <p className="text-palsustain-charcoal/80 mb-4">
                    {language === "en" ? founder.bio.en : founder.bio.ar}
                  </p>
                  
                  <blockquote className="italic text-palsustain-green border-l-4 border-palsustain-green pl-4 py-1 mb-4">
                    "{language === "en" ? founder.quote.en : founder.quote.ar}"
                  </blockquote>
                  
                  <div className="mb-4">
                    <h4 className="font-medium mb-2">
                      {language === "en" ? "Education" : "التعليم"}
                    </h4>
                    <ul className="text-sm text-palsustain-charcoal/80 space-y-1 list-disc pl-5">
                      {(language === "en" ? founder.education.en : founder.education.ar).map((edu, index) => (
                        <li key={index}>{edu}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="flex space-x-3 rtl:space-x-reverse">
                    {founder.social.instagram && (
                      <a 
                        href={`https://instagram.com/${founder.social.instagram}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 bg-palsustain-beige/30 rounded-full text-palsustain-green hover:bg-palsustain-beige/50 transition-colors"
                      >
                        <Instagram className="h-5 w-5" />
                      </a>
                    )}
                    {founder.social.linkedin && (
                      <a 
                        href={`https://linkedin.com/in/${founder.social.linkedin}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 bg-palsustain-beige/30 rounded-full text-palsustain-green hover:bg-palsustain-beige/50 transition-colors"
                      >
                        <Linkedin className="h-5 w-5" />
                      </a>
                    )}
                    {founder.social.twitter && (
                      <a 
                        href={`https://twitter.com/${founder.social.twitter}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 bg-palsustain-beige/30 rounded-full text-palsustain-green hover:bg-palsustain-beige/50 transition-colors"
                      >
                        <Twitter className="h-5 w-5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Mission & Vision */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-palsustain-green/10 border border-palsustain-green/20 rounded-xl p-6 md:p-8">
              <h3 className="text-2xl font-bold text-palsustain-green mb-4">
                {language === "en" ? "Our Mission" : "مهمتنا"}
              </h3>
              <p className="text-palsustain-charcoal/80">
                {language === "en" 
                  ? "To amplify Palestinian stories of resilience and sustainability through the framework of the Sustainable Development Goals, showcasing how communities innovate and persevere despite challenging circumstances." 
                  : "تضخيم القصص الفلسطينية عن المرونة والاستدامة من خلال إطار أهداف التنمية المستدامة، وإظهار كيف تبتكر المجتمعات وتثابر رغم الظروف الصعبة."
                }
              </p>
            </div>
            
            <div className="bg-palsustain-coral/10 border border-palsustain-coral/20 rounded-xl p-6 md:p-8">
              <h3 className="text-2xl font-bold text-palsustain-coral mb-4">
                {language === "en" ? "Our Vision" : "رؤيتنا"}
              </h3>
              <p className="text-palsustain-charcoal/80">
                {language === "en" 
                  ? "A world that recognizes and values Palestinian contributions to global sustainability efforts, where local knowledge and grassroots innovations are documented, celebrated, and can inspire others facing similar challenges." 
                  : "عالم يعترف ويقدر المساهمات الفلسطينية في جهود الاستدامة العالمية، حيث يتم توثيق المعرفة المحلية والابتكارات الشعبية والاحتفال بها، ويمكن أن تلهم الآخرين الذين يواجهون تحديات مماثلة."
                }
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default JourneyPage;
